import React, { useState, useEffect } from 'react';
import Confetti from 'react-confetti';
import './WordleGame.css';

function WordleGame({ playtime, gamePaused, setIsTimerActive, soundEffects, wordList, platform, encodeWord, decodeWord, saveGame, loadGame, gameId }) {
  const isIOS = platform === 'ios';
  const [wordLength, setWordLength] = useState(5);
  const [solution, setSolution] = useState('');
  const [guesses, setGuesses] = useState([]);
  const [currentGuess, setCurrentGuess] = useState('');
  const [evaluations, setEvaluations] = useState([]);
  const [gameStatus, setGameStatus] = useState('playing');
  const [notInSolution, setNotInSolution] = useState(new Set());
  const [currentGuessNumber, setCurrentGuessNumber] = useState(1);
  const [timer, setTimer] = useState(0);
  const [bestScore, setBestScore] = useState(() => {
    // Load best scores for each word length from localStorage
    const saved = localStorage.getItem('wordleBestScores');
    return saved ? JSON.parse(saved) : {};
  });
  const [keyboardStatus, setKeyboardStatus] = useState({});
  const [showPlayTogetherModal, setShowPlayTogetherModal] = useState(false);
  const [modalPaused, setModalPaused] = useState(false);
  const [shareCode, setShareCode] = useState('');
  const [joinCode, setJoinCode] = useState('');
  const [joinError, setJoinError] = useState('');
  const [isJoinedGame, setIsJoinedGame] = useState(false);
  const [joinedGameCode, setJoinedGameCode] = useState('');
  const [showDefinition, setShowDefinition] = useState(false);
  const [wordDefinition, setWordDefinition] = useState(null);
  const [definitionError, setDefinitionError] = useState(null);
  const [resumePrompt, setResumePrompt] = useState(false);
  const [savedGameData, setSavedGameData] = useState(null);
  
  const maxAttempts = 6;

  function getGuessEvaluation(guess, solution) {
    const solutionLetters = solution.split('');
    const guessLetters = guess.split('');
  
    const result = Array(wordLength).fill('absent');
    for (let i = 0; i < wordLength; i++) {
      if (guessLetters[i] === solutionLetters[i]) {
        result[i] = 'correct';
        solutionLetters[i] = null;
      }
    }
    for (let i = 0; i < wordLength; i++) {
      if (result[i] === 'correct') continue;
      const index = solutionLetters.indexOf(guessLetters[i]);
      if (index !== -1) {
        result[i] = 'present';
        solutionLetters[index] = null;
      }
    }
    return result;
  }

  const initializeGame = (length) => {
    const filteredWords = wordList.filter(word => word.length === length);
    console.log(`${length}-letter words:`, filteredWords);
    const sol = filteredWords[Math.floor(Math.random() * filteredWords.length)].toLowerCase();
    console.log(`solution: ${sol}`);
    setSolution(sol);
    setGuesses([]);
    setCurrentGuess('');
    setEvaluations([]);
    setGameStatus('playing');
    setNotInSolution(new Set());
    setIsTimerActive(true);
    setTimer(0);
    setCurrentGuessNumber(1);
    setKeyboardStatus({});
  };

  const handleWordLengthChange = (e) => {
    const newValue = e.target.value;
    if (newValue === 'multi') {
      handleOpenModal();
      return;
    }
    const newLength = parseInt(newValue);
    setWordLength(newLength);
    setKeyboardStatus({});
    setIsJoinedGame(false);
    setJoinedGameCode('');
    initializeGame(newLength);
  };

  useEffect(() => {
    const saved = loadGame(gameId);
    if (saved) {
      setResumePrompt(true);
      setSavedGameData(saved);
    } else {
      initializeGame(wordLength);
    }
  }, [gameId, loadGame]);

  useEffect(() => {
    if (gamePaused) {
      setIsTimerActive(false);
    } else if (gameStatus === 'playing') {
      setIsTimerActive(true);
    }
  }, [gamePaused, gameStatus, setIsTimerActive]);

  useEffect(() => {
    document.documentElement.style.setProperty('--word-length', wordLength);
  }, [wordLength]);

  useEffect(() => {
    let interval;
    if (gameStatus === 'playing' && !gamePaused && !modalPaused) {
      interval = setInterval(() => {
        setTimer(prev => prev + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [gameStatus, gamePaused, modalPaused]);

  useEffect(() => {
    if (gameStatus !== 'won') {
      setCurrentGuessNumber(guesses.length + 1);
    }
  }, [guesses, gameStatus]);

  const checkAndUpdateBestScore = (guessCount, timeElapsed) => {
    const currentWordLength = wordLength.toString();
    const currentBest = bestScore[currentWordLength];
    
    let shouldUpdate = false;
    
    if (!currentBest) {
      shouldUpdate = true;
    } else {
      // If current guess count is better, or equal with better time
      if (guessCount < currentBest.guesses || 
          (guessCount === currentBest.guesses && timeElapsed < currentBest.time)) {
        shouldUpdate = true;
      }
    }

    if (shouldUpdate) {
      const newBestScore = {
        ...bestScore,
        [currentWordLength]: {
          guesses: guessCount,
          time: timeElapsed
        }
      };
      setBestScore(newBestScore);
      localStorage.setItem('wordleBestScores', JSON.stringify(newBestScore));
    }
  };

  const onKeyPress = (key) => {
    if (gamePaused || gameStatus !== 'playing') return;
    if (key === 'ENTER' && currentGuess.length === wordLength) {
      if (!wordList.includes(currentGuess.toLowerCase())) {
        soundEffects.play('badMove');
        setCurrentGuess(''); 
        return; 
      }
      soundEffects.play('goodMove');
      const evalResult = getGuessEvaluation(currentGuess.toLowerCase(), solution);
      
      const newNotInSolution = new Set(notInSolution);
      currentGuess.toLowerCase().split('').forEach((letter, index) => {
        if (evalResult[index] === 'absent' && !solution.includes(letter)) {
          newNotInSolution.add(letter);
        }
      });
      setNotInSolution(newNotInSolution);

      const newKeyboardStatus = { ...keyboardStatus };
      currentGuess.toLowerCase().split('').forEach((letter, index) => {
        const currentStatus = newKeyboardStatus[letter];
        const newStatus = evalResult[index];
        
        if (newStatus === 'correct' || 
            (newStatus === 'present' && currentStatus !== 'correct')) {
          newKeyboardStatus[letter] = newStatus;
        } else if (!currentStatus && newStatus === 'absent') {
          newKeyboardStatus[letter] = 'absent';
        }
      });
      setKeyboardStatus(newKeyboardStatus);

      if (evalResult.every(e => e === 'correct')) {
        setGameStatus('won');
        soundEffects.play('gameWon');
        setIsTimerActive(false);
        checkAndUpdateBestScore(currentGuessNumber, timer);
        setGuesses([...guesses, currentGuess]);
        setEvaluations([...evaluations, evalResult]);
        setCurrentGuess('');
      } else {
        setGuesses([...guesses, currentGuess]);
        setEvaluations([...evaluations, evalResult]);
        setCurrentGuess('');
        if (guesses.length + 1 === maxAttempts) {
          setGameStatus('lost');
          soundEffects.play('gameOver');
          setIsTimerActive(false);
        }
      }
    } else if (key === 'DEL') {
      setCurrentGuess(currentGuess.slice(0, -1));
      soundEffects.play('lazer');
    } else if (currentGuess.length < wordLength && /^[a-zA-Z]$/.test(key)) {
      setCurrentGuess(currentGuess + key.toLowerCase());
      soundEffects.play('gameAction');
    }
  };

  const rows = [];
  for (let i = 0; i < maxAttempts; i++) {
    const guess = guesses[i] || (i === guesses.length ? currentGuess : '');
    const rowEval = evaluations[i] || [];
    const cells = [];
    for (let c = 0; c < wordLength; c++) {
      const letter = guess[c] || '';
      let className = 'cell';
      
      if (i === guesses.length) {
        className = `cell current`;
      } else if (letter) {
        className = rowEval[c] === 'correct' ? 'cell correct' :
                   rowEval[c] === 'present' ? 'cell present' :
                   rowEval[c] === 'absent' ? 'cell absent' : 'cell';
      }

      cells.push(
        <div key={c} className={className}>
          {letter}
        </div>
      );
    }
    rows.push(<div key={i} className="row">{cells}</div>);
  }

  const keyboardRows = [
    'q w e r t y u i o p'.split(' '),
    'a s d f g h j k l'.split(' '),
    ['ENTER', 'z', 'x', 'c', 'v', 'b', 'n', 'm', 'DEL']
  ];

  const handlePlayAgain = () => {
    soundEffects.play('playButton');
    setKeyboardStatus({});
    setIsJoinedGame(false);
    setJoinedGameCode('');
    initializeGame(wordLength);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleOpenModal = () => {
    setShowPlayTogetherModal(true);
    setModalPaused(true);
    setIsTimerActive(false);
  };

  const handleCloseModal = () => {
    setShowPlayTogetherModal(false);
    setModalPaused(false);
    if (gameStatus === 'playing' && !gamePaused) {
      setIsTimerActive(true);
    }
  };

  const handleJoinGame = (codeOverride) => {
    const codeToUse = codeOverride || joinCode.trim();
    const decodedWord = decodeWord(codeToUse);
    if (!decodedWord || !wordList.includes(decodedWord)) {
      setJoinError('Invalid game code');
      return;
    }
    setJoinError('');
    setSolution(decodedWord);
    setWordLength(decodedWord.length);
    setGuesses([]);
    setCurrentGuess('');
    setEvaluations([]);
    setGameStatus('playing');
    setNotInSolution(new Set());
    setIsTimerActive(true);
    setTimer(0);
    setCurrentGuessNumber(1);
    setKeyboardStatus({});
    setIsJoinedGame(true);
    setJoinedGameCode(codeToUse);
    handleCloseModal();
  };

  const fetchDefinition = async () => {
    try {
      const response = await fetch(`https://api.dictionaryapi.dev/api/v2/entries/en/${solution}`);
      if (!response.ok) {
        throw new Error('Definition not found');
      }
      const data = await response.json();
      setWordDefinition(data[0]);
      setDefinitionError(null);
      setShowDefinition(true);
    } catch (error) {
      setDefinitionError('Unable to find definition');
      setWordDefinition(null);
      setShowDefinition(true);
    }
  };

  const DefinitionModal = () => {
    if (!showDefinition) return null;
    
    const handleClose = (e) => {
        e.stopPropagation();
        setShowDefinition(false);
        soundEffects.play('generalButton');
    };
    
    return (
        <div 
            className="modal-overlay"
            style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
            onClick={handleClose}
        >
            <div 
                className="definition-modal"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="definition-content">
                    <div className="definition-header">
                        <h3>{solution} <span className="phonetic">{wordDefinition?.phonetic}</span></h3>
                        <button 
                            className="modal-cancel-btn"
                            onClick={handleClose}
                            aria-label="Close definition"
                        >
                            {/*✕*/}
                        </button>
                    </div>
                    {definitionError ? (
                        <p className="error-message">{definitionError}</p>
                    ) : (
                        <>
                            {wordDefinition?.meanings.map((meaning, index) => (
                                <div key={index} className="meaning-section">
                                    <p className="part-of-speech">{meaning.partOfSpeech}</p>
                                    <ol>
                                        {meaning.definitions.slice(0, 3).map((def, idx) => (
                                            <li key={idx}>
                                                <p>{def.definition}</p>
                                                {def.example && <p className="example">"{def.example}"</p>}
                                            </li>
                                        ))}
                                    </ol>
                                </div>
                            ))}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
  };

  const handleResumeGame = () => {
    if (savedGameData) {
      setSolution(savedGameData.solution);
      setGuesses(savedGameData.guesses);
      setCurrentGuess(savedGameData.currentGuess);
      setEvaluations(savedGameData.evaluations);
      setGameStatus(savedGameData.gameStatus);
      setNotInSolution(new Set(Array.from(savedGameData.notInSolution)));
      setTimer(savedGameData.timer);
      setCurrentGuessNumber(savedGameData.currentGuessNumber);
      setKeyboardStatus(savedGameData.keyboardStatus);
      setWordLength(savedGameData.wordLength);
      setIsJoinedGame(savedGameData.isJoinedGame);
      setJoinedGameCode(savedGameData.joinedGameCode);
      soundEffects.play('playButton');
    }
    setResumePrompt(false);
  };

  const handleNewGame = () => {
    localStorage.removeItem(`savedGame_${gameId}`);
    setResumePrompt(false);
    initializeGame(wordLength);
    soundEffects.play('generalButton');
  };

  useEffect(() => {
    return () => {
      if (gameStatus === 'playing' && solution !== '') {
        saveGame(gameId, {
          solution,
          guesses,
          currentGuess,
          evaluations,
          gameStatus,
          notInSolution: Array.from(notInSolution),
          timer,
          currentGuessNumber,
          keyboardStatus,
          wordLength,
          isJoinedGame,
          joinedGameCode
        });
        console.log('saved game',gameId, {
          solution,
          guesses,
          currentGuess,
          evaluations,
          gameStatus,
          notInSolution,
          timer,
          currentGuessNumber,
          keyboardStatus,
          wordLength,
          isJoinedGame,
          joinedGameCode
        });
      } else {
        localStorage.removeItem(`savedGame_${gameId}`);
      }
    };
  }, [guesses]);

  return (
    <div className="pre-wordle-game">
      {resumePrompt && (
        <div className="modal-overlay" style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}>
          <div className="modal-content resume-game-modal">
            <h2>Resume Game?</h2>
            <p>Would you like to continue your previous game?</p>
            <div className="modal-buttons">
              <button 
                onClick={handleResumeGame}
                className="play-btn"
              >
                Resume Game
              </button>
              <button 
                onClick={handleNewGame}
                className="back-btn"
              >
                New Game
              </button>
            </div>
          </div>
        </div>
      )}
      {gameStatus === 'won' && (
        <>
          <Confetti />
          <div className="win-message">YOU WIN!</div>
          <div className="game-message">
            <p>
              The word was "{solution}" 
              <span 
                className="dictionary-icon" 
                onClick={fetchDefinition}
                role="button" 
                aria-label="Look up definition"
              >
                📖
              </span>
            </p>
            <button className="restart-button" onClick={handlePlayAgain}>
              Play Again
            </button>
          </div>
          <DefinitionModal />
        </>
      )}
      <div className="wordle-game">
        <div className="header">
          <select value={wordLength} onChange={handleWordLengthChange}>
            <option value="multi">👥Play Together</option>
            <option value={3}>3 Letter Word</option>
            <option value={4}>4 Letter Word</option>
            <option value={5}>5 Letter Word</option>
            <option value={6}>6 Letter Word</option>
            <option value={7}>7 Letter Word</option>
            <option value={8}>8 Letter Word</option>
            <option value={9}>9 Letter Word</option>
            <option value={10}>10 Letter Word</option>
            <option value={11}>11 Letter Word</option>
            <option value={12}>12 Letter Word</option>
            {/*<option value={13}>13 Letter Word</option>*/}
          </select>
          {isJoinedGame && (
            <div className="join-code-overlay">
              👥{joinedGameCode}
            </div>
          )}
        </div>

        <div className={`game-info ${isIOS ? 'ios' : ''}`}>
          <div className="stats-row">
            <div>
              Guess: <span className={
                bestScore[wordLength] && 
                (currentGuessNumber < bestScore[wordLength].guesses || 
                 (currentGuessNumber === bestScore[wordLength].guesses && timer < bestScore[wordLength].time))
                ? 'better-score'
                : ''
              }>{currentGuessNumber}</span>
            </div>
            <div>
              Time: <span className={
                bestScore[wordLength] && 
                (currentGuessNumber < bestScore[wordLength].guesses || 
                 (currentGuessNumber === bestScore[wordLength].guesses && timer < bestScore[wordLength].time))
                ? 'better-score'
                : ''
              }>{formatTime(timer)}</span>
            </div>
          </div>
          <div className="best-row">
            Best: {bestScore[wordLength] 
              ? `${bestScore[wordLength].guesses} in ${formatTime(bestScore[wordLength].time)}` 
              : 'N/A'}
          </div>
        </div>
        <div className="board">{rows}</div>
        {gameStatus === 'playing' && (
          <div className="keyboard">
            {keyboardRows.map((row, i) => (
              <div key={i} className="kbRow">
                {row.map((k) => {
                  const status = keyboardStatus[k.toLowerCase()];
                  return (
                    <div
                      key={k}
                      className={`kbKey ${
                        status === 'correct' ? 'correct' :
                        status === 'present' ? 'present' :
                        status === 'absent' ? 'notInSolution' : ''
                      } ${k === 'ENTER' ? 'wide-key' : ''}`}
                      onClick={() => onKeyPress(k === 'ENTER' ? 'ENTER' : k === 'DEL' ? 'DEL' : k)}
                    >
                      {k === 'DEL' ? '⌫' : k}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        )}
      </div>
      {gameStatus === 'lost' && (
        <div className="wordle-game-over">
          <h2>Game Over!</h2>
          <p>The word was "{solution}".</p>
          <button className="restart-button" onClick={handlePlayAgain}>
            Try Again
          </button>
        </div>
      )}
      {showPlayTogetherModal && (
        <div className="play-together-modal">
          <div className="modal-content">
            <h2>Play Together Mode</h2>
            
            <div className="share-section">
              <h3>Share Current Game</h3>
              <p>Share this code with a friend to let them guess the same word:</p>
              <div className="code-display">
                <input 
                  type="text" 
                  value={encodeWord(solution)} 
                  readOnly
                  onClick={(e) => e.target.select()}
                />
                <button 
                  className="copy-btn"
                  onClick={() => {
                    navigator.clipboard.writeText(encodeWord(solution));
                    soundEffects.play('goodMove');
                  }}
                >
                  Copy
                </button>
                <button 
                  className="play-btn"
                  onClick={() => {
                    const currentGameCode = encodeWord(solution);
                    setJoinCode(currentGameCode);
                    handleJoinGame(currentGameCode);
                  }}
                >
                  Play
                </button>
              </div>
            </div>

            <div className="join-section">
              <h3>Join a Game</h3>
              <p>Enter a code shared with you:</p>
              <div className="code-input">
                <input
                  type="text"
                  value={joinCode}
                  onChange={(e) => {
                    setJoinCode(e.target.value);
                    setJoinError('');
                  }}
                  placeholder="Enter game code"
                />
                <button 
                  className="play-btn"
                  onClick={() => {
                    handleJoinGame();
                  }}
                >
                  Play
                </button>
              </div>
              {joinError && <p className="error-message">{joinError}</p>}
            </div>

            <button 
              className="modal-cancel-btn"
              onClick={handleCloseModal}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default WordleGame;
